import React, { useState, useEffect, useCallback } from 'react';
import {
  CreditCard,
  CheckCircle2,
  XCircle,
  AlertCircle,
  RefreshCw,
  ShieldCheck,
  Clock,
  Calendar,
  Ticket,
  X,
  Eye,
  Sparkles,
  Layers,
  Edit3,
  GraduationCap,
  BookOpen,
  Settings,
  School,
  Users,
  UserCheck
} from 'lucide-react';
import { CurrencyInput } from '../../components/admissions/AdmissionsFormInputs';
import '../admissions-admin/AdmissionsAdminDashboard.css';
import './AdmissionsFeeApprovals.css';

function parsePayload(val) {
  if (!val) return {};
  if (typeof val === 'object') return val;
  try {
    return JSON.parse(val);
  } catch (_) {
    return {};
  }
}

function formatRupiah(num) {
  const n = Number(num || 0);
  return 'Rp ' + n.toLocaleString('id-ID');
}

function formatDateDisplayEn(dStr) {
  if (!dStr) return '-';
  try {
    const d = new Date(dStr);
    if (isNaN(d.getTime())) return dStr;
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch (_) {
    return dStr;
  }
}

function formatDateTimeDisplayEn(dStr) {
  if (!dStr) return '-';
  try {
    const d = new Date(dStr);
    if (isNaN(d.getTime())) return dStr;
    const datePart = d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    const hours = String(d.getHours()).padStart(2, '0');
    const mins = String(d.getMinutes()).padStart(2, '0');
    return `${datePart}, ${hours}:${mins} WIB`;
  } catch (_) {
    return dStr;
  }
}

const CATEGORY_META = {
  periods: {
    title: 'Admissions Periods & Quotas Approval',
    desc: 'Authority to review and authorize admission periods, registration schedules, and seat quota allocations per education level.',
    icon: Calendar,
    color: '#2563eb',
    singular: 'Admission Period',
  },
  levels: {
    title: 'Education Level Fee Structures Approval',
    desc: 'Authority to review and authorize development fees, tuition fees, installment terms, and school bank accounts per education level.',
    icon: CreditCard,
    color: '#059669',
    singular: 'Fee Structure',
  },
  test_slots: {
    title: 'Entrance Assessment & Test Schedules Approval',
    desc: 'Authority to review and authorize offline observation & entrance test sessions, room allocations, and participant capacity per session.',
    icon: Clock,
    color: '#7c3aed',
    singular: 'Test Session',
  },
  promo_codes: {
    title: 'Promo Codes & Discount Vouchers Approval',
    desc: 'Authority to authorize registration fee discounts, development fee waivers, validity periods, and maximum redemption quotas.',
    icon: Ticket,
    color: '#d97706',
    singular: 'Promo Code',
  },
};

function cleanTargetTitle(label) {
  if (!label) return '';
  let str = label
    .replace(/^(Tambah|Perbarui|Hapus|Buat)\s+(Periode|Sesi\s+Tes|Jadwal\s+Tes|Kode\s+Promo|Tarif\s+Biaya|Voucher):\s*/i, '')
    .replace(/^(Tambah|Perbarui|Hapus|Buat)\s+(Periode|Sesi\s+Tes|Jadwal\s+Tes|Kode\s+Promo|Tarif\s+Biaya|Voucher):\s*/i, '');
  str = str.replace(/\s*\(([0-9]{4}\/[0-9]{4})\)\s*$/, (match, year) => {
    return str.indexOf(year) !== str.lastIndexOf(year) ? '' : match;
  });
  return str.trim();
}

export default function AdmissionsFeeApprovals({ apiBase, onFeeChange, initialCategory = 'levels' }) {
  const [activeCategory, setActiveCategory] = useState(initialCategory || 'levels');

  useEffect(() => {
    if (initialCategory) {
      setActiveCategory(initialCategory);
    }
  }, [initialCategory]);

  const [levels, setLevels] = useState([]);
  const [configApprovals, setConfigApprovals] = useState([]);
  const [overviewData, setOverviewData] = useState(null);
  const [applications, setApplications] = useState([]);
  const [modalPromoUsers, setModalPromoUsers] = useState([]);
  const [loadingPromoUsers, setLoadingPromoUsers] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);

  // Detail & Decision Modals (Clean Modal Pattern requested by user)
  const [activeModal, setActiveModal] = useState(null); // { type: 'period'|'level'|'test_slot'|'promo_code', item: any, isPending: boolean }
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectNote, setRejectNote] = useState('');
  const [feeModalTab, setFeeModalTab] = useState('fees'); // 'fees' | 'status_mat' | 'bank_sign'

  const loadData = useCallback(async () => {
    setLoading(true);
    setFeedback(null);
    try {
      const [resLevels, resConfigs, resOverview, resApps] = await Promise.all([
        fetch(`${apiBase}/admin/admissions/levels`, {
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        }),
        fetch(`${apiBase}/admin/admissions/config-approvals`, {
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        }),
        fetch(`${apiBase}/admin/admissions/overview`, {
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        }),
        fetch(`${apiBase}/admin/admissions/applications`, {
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        }),
      ]);

      const dataLevels = await resLevels.json();
      if (resLevels.ok && dataLevels?.ok && Array.isArray(dataLevels.data)) {
        setLevels(dataLevels.data);
      }

      if (resConfigs.ok) {
        const dataConfigs = await resConfigs.json();
        if (dataConfigs?.ok && Array.isArray(dataConfigs.data)) {
          setConfigApprovals(dataConfigs.data);
        }
      }

      if (resOverview && resOverview.ok) {
        const dataOverview = await resOverview.json();
        if (dataOverview?.ok && dataOverview.data) {
          setOverviewData(dataOverview.data);
        }
      }

      if (resApps && resApps.ok) {
        const dataApps = await resApps.json();
        if (dataApps?.ok && Array.isArray(dataApps.data)) {
          setApplications(dataApps.data);
        }
      }
    } catch (_) {
      setFeedback({ type: 'error', text: 'Gagal terhubung ke server untuk memuat data pengajuan approval' });
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  const getApplicantsForPeriod = useCallback((periodItem) => {
    if (!periodItem || !Array.isArray(applications)) return [];
    const targetId = periodItem.target_id || (!String(periodItem.id).startsWith('pending-') ? Number(periodItem.id) : null);
    const targetName = cleanTargetTitle(periodItem.period_name || periodItem.payload?.period_name || periodItem.target_label || '').toLowerCase();

    return applications.filter((app) => {
      if (targetId && Number(app.period_id) === Number(targetId)) return true;
      if (targetName && app.period_name && app.period_name.toLowerCase().includes(targetName)) return true;
      return false;
    });
  }, [applications]);

  const getApplicantsForSlot = useCallback((slotItem) => {
    if (!slotItem || !Array.isArray(applications)) return [];
    const targetId = slotItem.target_id || (!String(slotItem.id).startsWith('pending-') ? Number(slotItem.id) : null);
    const sName = cleanTargetTitle(slotItem.session_name || slotItem.payload?.session_name || slotItem.target_label || '').toLowerCase();
    const sDate = slotItem.test_date || slotItem.payload?.test_date;

    return applications.filter((app) => {
      if (targetId && (Number(app.test_slot_id) === Number(targetId) || Number(app.test_schedule?.slot_id) === Number(targetId))) {
        return true;
      }
      if (sName && app.test_schedule?.session_name && app.test_schedule.session_name.toLowerCase().includes(sName)) {
        return true;
      }
      if (sDate && app.test_schedule?.test_date === sDate) {
        return true;
      }
      return false;
    });
  }, [applications]);

  const getApplicantsForPromo = useCallback((promoItem) => {
    if (!promoItem || !Array.isArray(applications)) return [];
    const code = (promoItem.promo_code || promoItem.payload?.promo_code || '').toUpperCase().trim();
    if (!code) return [];
    return applications.filter((app) => {
      return (
        (app.promo_code_used && app.promo_code_used.toUpperCase().trim() === code) ||
        (app.promo_code && app.promo_code.toUpperCase().trim() === code)
      );
    });
  }, [applications]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Decision Handlers
  const handleApproveLevelFee = async (levelCode) => {
    if (!window.confirm(`Confirmation: Authorize and activate the new official fee structure for ${levelCode.toUpperCase()}?`)) {
      return;
    }
    setActionLoading(true);
    setFeedback(null);
    try {
      const res = await fetch(`${apiBase}/admin/admissions/levels/${levelCode}/approve-fees`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision: 'approve', note: 'Authorized and approved by Principal' }),
      });
      const data = await res.json();
      if (res.ok && data?.ok) {
        setFeedback({ type: 'success', text: data.message || 'Fee structure authorized successfully and is now active.' });
        setActiveModal(null);
        await loadData();
        if (onFeeChange) onFeeChange();
      } else {
        setFeedback({ type: 'error', text: data.message || 'Failed to authorize fee structure' });
      }
    } catch (_) {
      setFeedback({ type: 'error', text: 'Network error occurred while approving fee structure' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmRejectLevel = async (levelCode) => {
    const noteToSend = rejectNote.trim() || 'Fee revision rejected by Principal';
    setActionLoading(true);
    setFeedback(null);
    try {
      const res = await fetch(`${apiBase}/admin/admissions/levels/${levelCode}/approve-fees`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision: 'reject', note: noteToSend }),
      });
      const data = await res.json();
      if (res.ok && data?.ok) {
        setFeedback({ type: 'info', text: 'Fee revision request rejected and returned to Admin Admissions.' });
        setActiveModal(null);
        setRejectMode(false);
        setRejectNote('');
        await loadData();
        if (onFeeChange) onFeeChange();
      } else {
        setFeedback({ type: 'error', text: data.message || 'Failed to reject fee revision' });
      }
    } catch (_) {
      setFeedback({ type: 'error', text: 'Network error occurred while rejecting fee revision' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfigDecision = async (approvalId, decision, note = '') => {
    if (decision === 'approve' && !window.confirm('Confirmation: Authorize this submission and apply it immediately across all school systems?')) {
      return;
    }
    setActionLoading(true);
    setFeedback(null);
    try {
      const res = await fetch(`${apiBase}/admin/admissions/config-approvals/${approvalId}/decision`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision, note }),
      });
      const data = await res.json();
      if (res.ok && data?.ok) {
        setFeedback({ type: 'success', text: data.message || 'Pengajuan berhasil diproses' });
        setActiveModal(null);
        setRejectMode(false);
        setRejectNote('');
        await loadData();
        if (onFeeChange) onFeeChange();
      } else {
        setFeedback({ type: 'error', text: data.message || 'Gagal memproses approval' });
      }
    } catch (_) {
      setFeedback({ type: 'error', text: 'Terjadi kesalahan jaringan saat memproses pengajuan' });
    } finally {
      setActionLoading(false);
    }
  };

  // Lists
  const pendingLevels = levels.filter(
    (l) => l.fee_approval_status === 'pending_principal' || Boolean(l.pending_fee_payload)
  );
  const pendingPeriods = configApprovals.filter((c) => c.config_type === 'period');
  const pendingSlots = configApprovals.filter((c) => c.config_type === 'test_slot');
  const pendingPromos = configApprovals.filter((c) => c.config_type === 'promo_code');

  const activePeriodsList = (overviewData?.periods || []).filter((p) => !p.is_pending_approval && p.is_active);
  const activeSlotsList = (overviewData?.test_slots || []).filter((s) => !s.is_pending_approval && s.is_active);
  const activePromosList = (overviewData?.promo_codes || []).filter((pr) => !pr.is_pending_approval && pr.is_active);

  // Unified Periods: Merges active periods with their pending modifications so 1 period = 1 single row
  const newPeriodsPending = pendingPeriods.filter(
    (item) =>
      (item.action_type === 'create' || /^Tambah/i.test(item.target_label || '')) &&
      (!item.target_id || !activePeriodsList.some((p) => p.id === item.target_id))
  );
  const dedupedNewPeriodsPending = newPeriodsPending.filter((item, idx, self) => {
    const pName = (item.payload?.period_name || cleanTargetTitle(item.target_label) || '').trim().toLowerCase();
    return idx === self.findIndex((it) => (it.payload?.period_name || cleanTargetTitle(it.target_label) || '').trim().toLowerCase() === pName);
  });

  const existingPeriodsUnified = activePeriodsList.map((p) => {
    const pendingApproval = pendingPeriods.find(
      (item) =>
        item.target_id === p.id ||
        (item.target_label && item.target_label.includes(p.period_name))
    );
    return {
      ...p,
      pendingApproval: pendingApproval || null,
      isPendingUpdate: !!pendingApproval && pendingApproval.action_type !== 'delete' && !/^Hapus/i.test(pendingApproval.target_label || ''),
      isPendingDelete: !!pendingApproval && (pendingApproval.action_type === 'delete' || /^Hapus/i.test(pendingApproval.target_label || '')),
    };
  });

  const unifiedPeriods = [
    ...dedupedNewPeriodsPending.map((item) => ({
      id: `pending-${item.id}`,
      isNewPending: true,
      pendingApproval: item,
      period_name: cleanTargetTitle(item.target_label),
      academic_year: item.payload?.academic_year || '2026/2027',
      start_date: item.payload?.start_date,
      end_date: item.payload?.end_date,
      quotas: item.payload?.quotas || {},
      notes: item.payload?.notes || '',
      is_active: item.payload?.is_active ?? true,
      total_applicants: 0,
      requested_by_name: item.requested_by_name,
      created_at: item.created_at,
    })),
    ...existingPeriodsUnified,
  ].sort((a, b) => {
    const aNeedsAction = a.isNewPending || a.pendingApproval ? 1 : 0;
    const bNeedsAction = b.isNewPending || b.pendingApproval ? 1 : 0;
    return bNeedsAction - aNeedsAction;
  });

  // Unified Test Slots: Merges active test slots with their pending modifications so 1 session = 1 single row
  const newSlotsPending = pendingSlots.filter(
    (item) =>
      (item.action_type === 'create' || /^Tambah/i.test(item.target_label || '')) &&
      (!item.target_id || !activeSlotsList.some((s) => s.id === item.target_id))
  );
  const dedupedNewSlotsPending = newSlotsPending.filter((item, idx, self) => {
    const sName = (item.payload?.session_name || cleanTargetTitle(item.target_label) || '').trim().toLowerCase();
    const sDate = (item.payload?.test_date || '').trim();
    const key = `${sName}_${sDate}`;
    return idx === self.findIndex((it) => {
      const itName = (it.payload?.session_name || cleanTargetTitle(it.target_label) || '').trim().toLowerCase();
      const itDate = (it.payload?.test_date || '').trim();
      return `${itName}_${itDate}` === key;
    });
  });

  const existingSlotsUnified = activeSlotsList.map((s) => {
    const pendingApproval = pendingSlots.find(
      (item) =>
        item.target_id === s.id ||
        (item.target_label && item.target_label.includes(s.session_name))
    );
    return {
      ...s,
      pendingApproval: pendingApproval ? { ...pendingApproval, original: s } : null,
      isPendingUpdate: !!pendingApproval && pendingApproval.action_type !== 'delete' && !/^Hapus/i.test(pendingApproval.target_label || ''),
      isPendingDelete: !!pendingApproval && (pendingApproval.action_type === 'delete' || /^Hapus/i.test(pendingApproval.target_label || '')),
    };
  });

  const unifiedSlots = [
    ...dedupedNewSlotsPending.map((item) => ({
      id: `pending-${item.id}`,
      isNewPending: true,
      pendingApproval: item,
      session_name: item.payload?.session_name || cleanTargetTitle(item.target_label),
      test_date: item.payload?.test_date,
      test_time: item.payload?.test_time || '09:00 - 11:00 WIB',
      campus_location: item.payload?.campus_location || 'Simprug',
      room_name: item.payload?.room_name || 'Ruang Assessment',
      slot_quota: item.payload?.slot_quota || 15,
      booked_count: 0,
      level_code: item.payload?.level_code || 'all',
      custom_notes: item.payload?.custom_notes || '',
      requested_by_name: item.requested_by_name,
      created_at: item.created_at,
    })),
    ...existingSlotsUnified,
  ].sort((a, b) => {
    const aNeedsAction = a.isNewPending || a.pendingApproval ? 1 : 0;
    const bNeedsAction = b.isNewPending || b.pendingApproval ? 1 : 0;
    return bNeedsAction - aNeedsAction;
  });

  // Unified Promo Codes: Merges active promo codes with their pending modifications so 1 promo = 1 single row
  const newPromosPending = pendingPromos.filter(
    (item) =>
      (item.action_type === 'create' || /^Tambah/i.test(item.target_label || '')) &&
      (!item.target_id || !activePromosList.some((pr) => pr.id === item.target_id))
  );
  const dedupedNewPromosPending = newPromosPending.filter((item, idx, self) => {
    const code = (item.payload?.promo_code || cleanTargetTitle(item.target_label) || '').trim().toUpperCase();
    return idx === self.findIndex((it) => (it.payload?.promo_code || cleanTargetTitle(it.target_label) || '').trim().toUpperCase() === code);
  });

  const existingPromosUnified = activePromosList.map((pr) => {
    const pendingApproval = pendingPromos.find(
      (item) =>
        item.target_id === pr.id ||
        (item.target_label && item.target_label.includes(pr.promo_code)) ||
        (item.payload?.promo_code && item.payload.promo_code === pr.promo_code)
    );
    return {
      ...pr,
      pendingApproval: pendingApproval ? { ...pendingApproval, original: pr } : null,
      isPendingUpdate: !!pendingApproval && pendingApproval.action_type !== 'delete' && !/^Hapus/i.test(pendingApproval.target_label || ''),
      isPendingDelete: !!pendingApproval && (pendingApproval.action_type === 'delete' || /^Hapus/i.test(pendingApproval.target_label || '')),
    };
  });

  const unifiedPromos = [
    ...dedupedNewPromosPending.map((item) => ({
      id: `pending-${item.id}`,
      isNewPending: true,
      pendingApproval: item,
      promo_code: item.payload?.promo_code || 'VOUCHER',
      promo_title: item.payload?.promo_title || cleanTargetTitle(item.target_label),
      discount_type: item.payload?.discount_type || 'percentage',
      discount_value: item.payload?.discount_value || 0,
      applies_to_levels: item.payload?.applies_to_levels || 'all',
      valid_until: item.payload?.valid_until || null,
      max_quota: item.payload?.max_quota || 100,
      used_count: 0,
      is_active: item.payload?.is_active ?? true,
      requested_by_name: item.requested_by_name,
      created_at: item.created_at,
    })),
    ...existingPromosUnified,
  ].sort((a, b) => {
    const aNeedsAction = a.isNewPending || a.pendingApproval ? 1 : 0;
    const bNeedsAction = b.isNewPending || b.pendingApproval ? 1 : 0;
    return bNeedsAction - aNeedsAction;
  });

  const currentMeta = CATEGORY_META[activeCategory] || CATEGORY_META.periods;
  const CategoryIcon = currentMeta.icon;

  const activeCategoryPendingCount =
    activeCategory === 'periods'
      ? unifiedPeriods.filter((p) => p.isNewPending || p.pendingApproval).length
      : activeCategory === 'levels'
      ? pendingLevels.length
      : activeCategory === 'test_slots'
      ? unifiedSlots.filter((s) => s.isNewPending || s.pendingApproval).length
      : unifiedPromos.filter((pr) => pr.isNewPending || pr.pendingApproval).length;

  const isEnglishCategory = true;

  const openModal = async (type, item, isPending) => {
    setRejectMode(false);
    setRejectNote('');
    setFeeModalTab('fees');
    setModalPromoUsers([]);
    setActiveModal({ type, item, isPending });

    if (type === 'promo_code') {
      const promoId = item.id;
      if (promoId && !String(promoId).startsWith('pending-')) {
        try {
          setLoadingPromoUsers(true);
          const res = await fetch(`${apiBase}/admin/admissions/promo-codes/${promoId}/users`, {
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
          });
          if (res.ok) {
            const data = await res.json();
            if (data?.ok && Array.isArray(data.users)) {
              setModalPromoUsers(data.users);
            }
          }
        } catch (_) {
          // Fall back to applications list filter
        } finally {
          setLoadingPromoUsers(false);
        }
      }
    }
  };

  const closeModal = () => {
    setActiveModal(null);
    setRejectMode(false);
    setRejectNote('');
    setFeeModalTab('fees');
    setModalPromoUsers([]);
  };

  return (
    <div className="adm-fee-approvals-wrap">
      {/* Header */}
      <div className="afa-header">
        <div className="afa-title-block">
          <div className="afa-icon-badge" style={{ background: currentMeta.color }}>
            <CategoryIcon size={22} color="#ffffff" />
          </div>
          <div>
            <div className="afa-title-row">
              <h2>{currentMeta.title}</h2>
              {activeCategoryPendingCount > 0 ? (
                <span className="afa-header-status-badge pending">
                  <Clock size={12} />
                  <span>
                    {isEnglishCategory
                      ? `${activeCategoryPendingCount} Pending Decision`
                      : `${activeCategoryPendingCount} Menunggu Persetujuan`}
                  </span>
                </span>
              ) : (
                <span className="afa-header-status-badge approved">
                  <CheckCircle2 size={12} />
                  <span>
                    {isEnglishCategory
                      ? `All ${currentMeta.singular}s Approved`
                      : `Semua ${currentMeta.singular} Disetujui`}
                  </span>
                </span>
              )}
            </div>
            <p>{currentMeta.desc}</p>
          </div>
        </div>
        <button
          type="button"
          className="afa-refresh-btn"
          onClick={loadData}
          disabled={loading || actionLoading}
          title={isEnglishCategory ? 'Refresh data' : 'Segarkan data'}
        >
          <RefreshCw size={14} className={loading ? 'spin' : ''} />
          <span>{isEnglishCategory ? 'Refresh Data' : 'Segarkan Data'}</span>
        </button>
      </div>

      {feedback && (
        <div className={`afa-feedback-alert ${feedback.type}`}>
          {feedback.type === 'success' && <CheckCircle2 size={16} />}
          {feedback.type === 'error' && <XCircle size={16} />}
          {feedback.type === 'info' && <AlertCircle size={16} />}
          <span>{feedback.text}</span>
          <button type="button" className="afa-alert-close" onClick={() => setFeedback(null)}>
            <X size={14} />
          </button>
        </div>
      )}

      {/* ========================================================
          CATEGORY 1: ADMISSIONS PERIODS APPROVAL (UNIFIED TABLE)
          ======================================================== */}
      {activeCategory === 'periods' && (
        <div className="afa-table-card">
          <div className="afa-table-responsive">
            <table className="afa-clean-table">
              <thead>
                <tr>
                  <th>Admission Period</th>
                  <th>Academic Year</th>
                  <th>Registration Period</th>
                  <th>Seat Quota Allocation</th>
                  <th>Status / Action Required</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {unifiedPeriods.map((p) => {
                  const isActionNeeded = p.isNewPending || !!p.pendingApproval;
                  const isCreate = p.isNewPending;
                  const isUpdate = p.isPendingUpdate;
                  const isDelete = p.isPendingDelete;

                  const payload = p.pendingApproval?.payload;
                  const original = p.pendingApproval?.original;

                  const displayAcademicYear = payload?.academic_year || p.academic_year || '2026/2027';
                  const displayStartDate = payload?.start_date || p.start_date;
                  const displayEndDate = payload?.end_date || p.end_date;
                  const displayQuotas = payload?.quotas || p.quotas || {};
                  const periodApplicants = getApplicantsForPeriod(p);
                  const totalInWave = periodApplicants.length || (p.total_applicants !== undefined ? p.total_applicants : Object.values(p.filled || {}).reduce((a, b) => a + Number(b), 0));

                  const hasDateChange = isUpdate && original && (original.start_date !== displayStartDate || original.end_date !== displayEndDate);
                  const hasQuotaChange = isUpdate && original?.quotas && JSON.stringify(original.quotas) !== JSON.stringify(displayQuotas);

                  return (
                    <tr key={p.id} className={isActionNeeded ? 'afa-row-pending-highlight' : ''}>
                      <td>
                        <div className="afa-cell-title-row">
                          {isCreate && <span className="afa-tag-action create">+ New Period</span>}
                          {isUpdate && <span className="afa-tag-action update">✎ Modified Period</span>}
                          {isDelete && <span className="afa-tag-action delete">✕ Deletion Request</span>}
                          {!isActionNeeded && <span className="afa-tag-action active">Official &amp; Active</span>}
                          <strong>{cleanTargetTitle(p.period_name)}</strong>
                        </div>
                        {isActionNeeded ? (
                          <div className="afa-cell-meta-sub">
                            {isCreate && `Submitted by: ${p.pendingApproval?.requested_by_name || 'Admin Admissions'} • ${formatDateDisplayEn(p.pendingApproval?.created_at)}`}
                            {isUpdate && `Modification submitted by: ${p.pendingApproval?.requested_by_name || 'Admin Admissions'} • ${formatDateDisplayEn(p.pendingApproval?.created_at)}`}
                            {isDelete && `Deletion requested by: ${p.pendingApproval?.requested_by_name || 'Admin Admissions'} • ${formatDateDisplayEn(p.pendingApproval?.created_at)}`}
                          </div>
                        ) : (
                          p.notes && <div className="afa-cell-meta-sub">{p.notes}</div>
                        )}
                      </td>
                      <td>
                        <span className="afa-year-pill">{displayAcademicYear}</span>
                      </td>
                      <td>
                        <div className="afa-date-range">
                          <span>{formatDateDisplayEn(displayStartDate)}</span>
                          <span className="afa-date-sep">to</span>
                          <span>{formatDateDisplayEn(displayEndDate)}</span>
                        </div>
                        {hasDateChange && (
                          <div className="afa-diff-sub-table">
                            Was: {formatDateDisplayEn(original.start_date)} - {formatDateDisplayEn(original.end_date)}
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="afa-pills-row">
                          <span className="afa-pill ps">PS: {displayQuotas.preschool ?? 0}</span>
                          <span className="afa-pill kg">KG: {displayQuotas.kindergarten ?? 0}</span>
                          <span className="afa-pill pri">PRI: {displayQuotas.primary ?? 0}</span>
                          <span className="afa-pill sec">SEC: {displayQuotas.secondary ?? 0}</span>
                        </div>
                        {hasQuotaChange && (
                          <div className="afa-diff-sub-table">
                            Quota adjusted
                          </div>
                        )}
                      </td>
                      <td>
                        {isActionNeeded ? (
                          <div className="afa-status-col">
                            <span className="afa-status-badge pending">
                              <Clock size={12} />
                              <span>Pending Decision</span>
                            </span>
                            <span className="afa-badge-action-needed">Action Required</span>
                          </div>
                        ) : (
                          <span className="afa-status-badge approved">
                            <CheckCircle2 size={12} />
                            <span>Active ({totalInWave} Students)</span>
                          </span>
                        )}
                      </td>
                      <td className="text-right">
                        {isActionNeeded ? (
                          <button
                            type="button"
                            className="afa-btn-action-primary"
                            onClick={() => openModal('period', p.pendingApproval, true)}
                          >
                            Review &amp; Decide
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="afa-btn-action-secondary"
                            onClick={() => openModal('period', p, false)}
                          >
                            <Eye size={13} />
                            <span>View Details</span>
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}

                {unifiedPeriods.length === 0 && (
                  <tr>
                    <td colSpan="6" className="afa-empty-text">No admission periods registered in the system</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ========================================================
          CATEGORY 2: MASTER JENJANG PENDIDIKAN & BIAYA (TABEL PERSIS ADMIN ADMISSIONS)
          ======================================================== */}
      {activeCategory === 'levels' && (() => {
        const sortedLevels = [...levels].sort((a, b) => {
          const aPending = a.fee_approval_status === 'pending_principal' || Boolean(a.pending_fee_payload) ? 1 : 0;
          const bPending = b.fee_approval_status === 'pending_principal' || Boolean(b.pending_fee_payload) ? 1 : 0;
          return bPending - aPending;
        });

        return (
          <div className="adm-table-card">
            <div className="adm-data-table-wrap">
              <table className="adm-data-table">
                <thead>
                  <tr>
                    <th>Education Level</th>
                    <th>Total Initial Upfront (Parent Cost)</th>
                    <th>Monthly Tuition (SPP)</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedLevels.map((lvl) => {
                    const isPending = lvl.fee_approval_status === 'pending_principal' || Boolean(lvl.pending_fee_payload);
                    const pending = isPending ? parsePayload(lvl.pending_fee_payload) : {};

                    // Current official rates
                    const currReg = Number(lvl.registration_fee || 0);
                    const currTest = Number(lvl.test_reg_form_fee || 0);
                    const currAdm = Number(lvl.admission_fee || 0);
                    const currDev = Number(lvl.development_fee || 0);
                    const currDf1 = Number(lvl.df_1_amount || 0);
                    const currTuition = Number(lvl.tuition_fee || 0);
                    const currInitialUpfront = currReg + currTest + currAdm + (currDf1 > 0 ? currDf1 : currDev);

                    // Proposed / display rates
                    const regFee = isPending && pending.registration_fee !== undefined ? Number(pending.registration_fee) : currReg;
                    const testFee = isPending && pending.test_reg_form_fee !== undefined ? Number(pending.test_reg_form_fee) : currTest;
                    const admFee = isPending && pending.admission_fee !== undefined ? Number(pending.admission_fee) : currAdm;
                    const devFee = isPending && pending.development_fee !== undefined ? Number(pending.development_fee) : currDev;
                    const df1 = isPending && pending.df_1_amount !== undefined ? Number(pending.df_1_amount) : currDf1;
                    const tuitionFee = isPending && pending.tuition_fee !== undefined ? Number(pending.tuition_fee) : currTuition;
                    const initialParentUpfront = regFee + testFee + admFee + (df1 > 0 ? df1 : devFee);

                    const hasUpfrontChange = isPending && initialParentUpfront !== currInitialUpfront;
                    const hasTuitionChange = isPending && tuitionFee !== currTuition;

                    return (
                      <tr key={lvl.level_code} style={isPending ? { background: '#fffbeb' } : undefined}>
                        <td>
                          <strong>{lvl.level_name}</strong>
                          <div className="adm-sub-text">
                            {lvl.tag_code} &bull; {lvl.assessment_title || 'Standard Assessment'}
                          </div>
                          {isPending && (
                            <div className="afa-diff-sub-table">
                              Submitted: {formatDateTimeDisplayEn(lvl.fee_updated_at)} by {lvl.fee_updated_by || 'Admin Admissions'}
                            </div>
                          )}
                        </td>
                        <td>
                          <div>
                            <strong style={{ fontSize: '0.94rem', color: '#0f172a' }}>{formatRupiah(initialParentUpfront)}</strong>
                            <div className="adm-sub-text" style={{ fontSize: '0.72rem', marginTop: 2 }}>
                              Reg &amp; Test: {formatRupiah(regFee + testFee)} &bull; Matr: {formatRupiah(admFee)} &bull; DF Term 1: {formatRupiah(df1 > 0 ? df1 : devFee)}
                            </div>
                            {hasUpfrontChange && (
                              <div className="afa-diff-sub-table">
                                Was: {formatRupiah(currInitialUpfront)}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <div>
                            <strong style={{ fontSize: '0.92rem', color: '#0f172a' }}>{formatRupiah(tuitionFee)}</strong>
                            <span className="adm-sub-text" style={{ marginLeft: 4 }}>/ mo</span>
                            {hasTuitionChange && (
                              <div className="afa-diff-sub-table">
                                Was: {formatRupiah(currTuition)} / mo
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          {isPending ? (
                            <span className="adm-status-pill adm-badge-orange">Pending Decision</span>
                          ) : lvl.fee_approval_status === 'approved' ? (
                            <span className="adm-status-pill adm-badge-green">Official &amp; Active</span>
                          ) : lvl.fee_approval_status === 'rejected' ? (
                            <span className="adm-status-pill adm-badge-red">Revision Required</span>
                          ) : (
                            <span className="adm-status-pill adm-badge-gray">Draft</span>
                          )}
                        </td>
                        <td>
                          <div className="adm-cell-actions" style={{ justifyContent: 'flex-end' }}>
                            {isPending ? (
                              <button
                                type="button"
                                className="adm-btn-action-edit"
                                onClick={() => openModal('level', lvl, true)}
                              >
                                <Edit3 size={13} />
                                <span>Review &amp; Decide</span>
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="adm-btn-action-view"
                                onClick={() => openModal('level', lvl, false)}
                              >
                                <Eye size={13} />
                                <span>View Details</span>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {sortedLevels.length === 0 && (
                    <tr>
                      <td colSpan="5" style={{ textAlign: 'center', padding: '24px', color: '#94a3b8' }}>
                        No education levels registered in the system
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* ========================================================
          CATEGORY 3: ENTRANCE TEST SESSIONS (UNIFIED TABLE)
          ======================================================== */}
      {activeCategory === 'test_slots' && (
        <div className="adm-table-card">
          <div className="adm-data-table-wrap">
            <table className="adm-data-table">
              <thead>
                <tr>
                  <th>Session Name &amp; Room</th>
                  <th>Target Level</th>
                  <th>Test Schedule &amp; Date</th>
                  <th>Capacity &amp; Booked</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {unifiedSlots.map((s) => {
                  const isActionNeeded = s.isNewPending || !!s.pendingApproval;
                  const isCreate = s.isNewPending || s.pendingApproval?.action_type === 'create' || /^Tambah/i.test(s.pendingApproval?.target_label || '');
                  const isUpdate = s.isPendingUpdate || (s.pendingApproval?.action_type === 'update' && !isCreate);
                  const isDelete = s.isPendingDelete || s.pendingApproval?.action_type === 'delete' || /^Hapus/i.test(s.pendingApproval?.target_label || '');

                  const payload = s.pendingApproval?.payload || {};
                  const orig = s.pendingApproval?.original || null;

                  const displaySessionName = payload?.session_name || s.session_name;
                  const displayDate = payload?.test_date || s.test_date;
                  const displayTime = payload?.test_time || s.test_time || '09:00 - 11:00 WIB';
                  const displayCampus = payload?.campus_location || s.campus_location || 'Simprug';
                  const displayRoom = payload?.room_name || s.room_name || 'Ruang Assessment';
                  const displayQuota = payload?.slot_quota !== undefined ? Number(payload.slot_quota) : Number(s.slot_quota || 15);
                  const displayLevel = payload?.level_code || s.level_code || 'all';

                  const hasDateChange = isUpdate && orig && (orig.test_date !== displayDate || orig.test_time !== displayTime);
                  const hasRoomChange = isUpdate && orig && (orig.room_name !== displayRoom || orig.campus_location !== displayCampus);
                  const hasQuotaChange = isUpdate && orig && Number(orig.slot_quota) !== displayQuota;
                  const hasLevelChange = isUpdate && orig && orig.level_code !== displayLevel;

                  const slotAttendees = getApplicantsForSlot(s);
                  const bookedCount = slotAttendees.length || Number(s.booked_count || 0);
                  const isFull = !isActionNeeded && bookedCount >= displayQuota;

                  return (
                    <tr key={s.id} style={isActionNeeded ? { background: '#fffbeb' } : undefined}>
                      <td>
                        <div className="afa-cell-title-row">
                          {isCreate && <span className="afa-tag-action create">+ New Session</span>}
                          {isUpdate && <span className="afa-tag-action update">✎ Modified Session</span>}
                          {isDelete && <span className="afa-tag-action delete">✕ Deletion Request</span>}
                          {!isActionNeeded && <span className="afa-tag-action active">Official &amp; Active</span>}
                          <strong>{cleanTargetTitle(displaySessionName)}</strong>
                        </div>
                        {isActionNeeded ? (
                          <div className="adm-sub-text">
                            {isCreate && `Submitted by: ${s.pendingApproval?.requested_by_name || 'Admin Admissions'} • ${formatDateDisplayEn(s.pendingApproval?.created_at)}`}
                            {isUpdate && `Modification submitted by: ${s.pendingApproval?.requested_by_name || 'Admin Admissions'} • ${formatDateDisplayEn(s.pendingApproval?.created_at)}`}
                            {isDelete && `Deletion requested by: ${s.pendingApproval?.requested_by_name || 'Admin Admissions'} • ${formatDateDisplayEn(s.pendingApproval?.created_at)}`}
                          </div>
                        ) : null}
                        <div className="adm-sub-text">
                          {displayCampus?.split(' - ')[0]} &bull; {displayRoom || 'Assessment Room'}
                        </div>
                        {hasRoomChange && (
                          <div className="afa-diff-sub-table">
                            Was: {orig.campus_location?.split(' - ')[0]} &bull; {orig.room_name}
                          </div>
                        )}
                      </td>
                      <td>
                        <span className="adm-mono">
                          {displayLevel === 'all' || !displayLevel ? 'All Levels' : displayLevel.toUpperCase()}
                        </span>
                        {hasLevelChange && (
                          <div className="afa-diff-sub-table">
                            Was: {orig.level_code?.toUpperCase() || 'ALL'}
                          </div>
                        )}
                      </td>
                      <td>
                        <div>
                          <strong>{formatDateDisplayEn(displayDate)}</strong>
                          <div className="adm-sub-text">{displayTime}</div>
                        </div>
                        {hasDateChange && (
                          <div className="afa-diff-sub-table">
                            Was: {formatDateDisplayEn(orig.test_date)}, {orig.test_time}
                          </div>
                        )}
                      </td>
                      <td>
                        {isActionNeeded ? (
                          <div>
                            <strong>{displayQuota} Seats</strong>
                            {hasQuotaChange && (
                              <div className="afa-diff-sub-table">
                                Was: {orig.slot_quota} Seats
                              </div>
                            )}
                          </div>
                        ) : (
                          <div>
                            <span className={`adm-cap-badge ${isFull ? 'full' : 'avail'}`}>
                              {bookedCount} / {displayQuota} Participants
                            </span>
                          </div>
                        )}
                      </td>
                      <td>
                        {isActionNeeded ? (
                          <span className="adm-status-pill adm-badge-orange">Pending Decision</span>
                        ) : isFull ? (
                          <span className="adm-status-pill adm-badge-red">Full (Closed)</span>
                        ) : (
                          <span className="adm-status-pill adm-badge-green">Official &amp; Active</span>
                        )}
                      </td>
                      <td>
                        <div className="adm-cell-actions" style={{ justifyContent: 'flex-end' }}>
                          {isActionNeeded ? (
                            <button
                              type="button"
                              className="adm-btn-action-edit"
                              onClick={() => {
                                const itemToOpen = { ...s.pendingApproval, original: s };
                                openModal('test_slot', itemToOpen, true);
                              }}
                            >
                              <Edit3 size={13} />
                              <span>Review &amp; Decide</span>
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="adm-btn-action-view"
                              onClick={() => openModal('test_slot', s, false)}
                            >
                              <Eye size={13} />
                              <span>View Details</span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}

                {unifiedSlots.length === 0 && (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center', padding: '24px', color: '#94a3b8' }}>
                      No entrance test sessions registered in the system
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ========================================================
          CATEGORY 4: PROMO CODES & DISCOUNTS (UNIFIED TABLE)
          ======================================================== */}
      {activeCategory === 'promo_codes' && (
        <div className="adm-table-card">
          <div className="adm-data-table-wrap">
            <table className="adm-data-table">
              <thead>
                <tr>
                  <th>Voucher Code &amp; Program</th>
                  <th>Discount Benefit</th>
                  <th>Applicable Levels</th>
                  <th>Validity &amp; Quota</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {unifiedPromos.map((pr) => {
                  const isActionNeeded = pr.isNewPending || !!pr.pendingApproval;
                  const isCreate = pr.isNewPending || pr.pendingApproval?.action_type === 'create' || /^Tambah/i.test(pr.pendingApproval?.target_label || '');
                  const isUpdate = pr.isPendingUpdate || (pr.pendingApproval?.action_type === 'update' && !isCreate);
                  const isDelete = pr.isPendingDelete || pr.pendingApproval?.action_type === 'delete' || /^Hapus/i.test(pr.pendingApproval?.target_label || '');

                  const payload = pr.pendingApproval?.payload || {};
                  const orig = pr.pendingApproval?.original || null;

                  const displayPromoCode = payload?.promo_code || pr.promo_code || 'VOUCHER';
                  const displayTitle = payload?.promo_title || pr.promo_title || cleanTargetTitle(pr.target_label);
                  const displayDiscountType = payload?.discount_type || pr.discount_type || 'percentage';
                  const displayDiscountValue = payload?.discount_value !== undefined ? payload.discount_value : pr.discount_value;
                  const displayLevels = payload?.applies_to_levels || pr.applies_to_levels || 'all';
                  const displayValidUntil = payload?.valid_until !== undefined ? payload.valid_until : pr.valid_until;
                  const displayQuota = payload?.max_quota !== undefined ? Number(payload.max_quota) : Number(pr.max_quota || 100);

                  const hasDiscountChange = isUpdate && orig && (orig.discount_value !== displayDiscountValue || orig.discount_type !== displayDiscountType);
                  const hasLevelsChange = isUpdate && orig && orig.applies_to_levels !== displayLevels;
                  const hasValidChange = isUpdate && orig && orig.valid_until !== displayValidUntil;
                  const hasQuotaChange = isUpdate && orig && Number(orig.max_quota) !== displayQuota;

                  const isExpired = !isActionNeeded && displayValidUntil && new Date(displayValidUntil) < new Date();
                  const promoApplicants = getApplicantsForPromo(pr);
                  const usedCount = promoApplicants.length || Number(pr.used_count || 0);
                  const isFull = !isActionNeeded && usedCount >= displayQuota;

                  return (
                    <tr key={pr.id} style={isActionNeeded ? { background: '#fffbeb' } : undefined}>
                      <td>
                        <div className="afa-cell-title-row">
                          {isCreate && <span className="afa-tag-action create">+ New Voucher</span>}
                          {isUpdate && <span className="afa-tag-action update">✎ Modified Voucher</span>}
                          {isDelete && <span className="afa-tag-action delete">✕ Deletion Request</span>}
                          {!isActionNeeded && <span className="afa-tag-action active">Official &amp; Active</span>}
                          <strong className="adm-mono" style={{ color: '#2563eb', fontSize: '0.92rem' }}>
                            {displayPromoCode}
                          </strong>
                        </div>
                        <div style={{ fontWeight: 600, color: '#1e293b', marginTop: 2, fontSize: '0.85rem' }}>
                          {cleanTargetTitle(displayTitle)}
                        </div>
                        {isActionNeeded && (
                          <div className="adm-sub-text">
                            {isCreate && `Submitted by: ${pr.pendingApproval?.requested_by_name || 'Admin Admissions'} • ${formatDateDisplayEn(pr.pendingApproval?.created_at)}`}
                            {isUpdate && `Modification submitted by: ${pr.pendingApproval?.requested_by_name || 'Admin Admissions'} • ${formatDateDisplayEn(pr.pendingApproval?.created_at)}`}
                            {isDelete && `Deletion requested by: ${pr.pendingApproval?.requested_by_name || 'Admin Admissions'} • ${formatDateDisplayEn(pr.pendingApproval?.created_at)}`}
                          </div>
                        )}
                      </td>
                      <td>
                        <strong className="adm-mono" style={{ color: '#059669', fontSize: '0.9rem' }}>
                          {displayDiscountType === 'percentage'
                            ? `${displayDiscountValue}% from DF`
                            : formatRupiah(displayDiscountValue)}
                        </strong>
                        {hasDiscountChange && (
                          <div className="afa-diff-sub-table">
                            Was: {orig.discount_type === 'percentage' ? `${orig.discount_value}% from DF` : formatRupiah(orig.discount_value)}
                          </div>
                        )}
                      </td>
                      <td>
                        <span className="adm-mono">
                          {displayLevels === 'all' || !displayLevels ? 'All Levels' : displayLevels.toUpperCase()}
                        </span>
                        {hasLevelsChange && (
                          <div className="afa-diff-sub-table">
                            Was: {orig.applies_to_levels?.toUpperCase() || 'ALL'}
                          </div>
                        )}
                      </td>
                      <td>
                        <div>
                          {displayValidUntil ? `Until ${formatDateDisplayEn(displayValidUntil)}` : 'Lifetime (No Expiry)'}
                        </div>
                        <div className="adm-sub-text">
                          Quota: {usedCount} / {displayQuota} Used
                        </div>
                        {hasValidChange && (
                          <div className="afa-diff-sub-table">
                            Was: {orig.valid_until ? formatDateDisplayEn(orig.valid_until) : 'Lifetime'}
                          </div>
                        )}
                        {hasQuotaChange && (
                          <div className="afa-diff-sub-table">
                            Was: {orig.max_quota} Quota
                          </div>
                        )}
                      </td>
                      <td>
                        {isActionNeeded ? (
                          <span className="adm-status-pill adm-badge-orange">Pending Decision</span>
                        ) : isExpired ? (
                          <span className="adm-status-pill adm-badge-gray">Expired</span>
                        ) : isFull ? (
                          <span className="adm-status-pill adm-badge-red">Quota Exhausted</span>
                        ) : (
                          <span className="adm-status-pill adm-badge-green">Official &amp; Active</span>
                        )}
                      </td>
                      <td>
                        <div className="adm-cell-actions" style={{ justifyContent: 'flex-end' }}>
                          {isActionNeeded ? (
                            <button
                              type="button"
                              className="adm-btn-action-edit"
                              onClick={() => {
                                const itemToOpen = { ...pr.pendingApproval, original: pr };
                                openModal('promo_code', itemToOpen, true);
                              }}
                            >
                              <Edit3 size={13} />
                              <span>Review &amp; Decide</span>
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="adm-btn-action-view"
                              onClick={() => openModal('promo_code', pr, false)}
                            >
                              <Eye size={13} />
                              <span>View Details</span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}

                {unifiedPromos.length === 0 && (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center', padding: '24px', color: '#94a3b8' }}>
                      No promo codes registered in the system
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ========================================================
          MODAL PERSIS ADMIN ADMISSIONS UNTUK JENJANG & BIAYA
          ======================================================== */}
      {activeModal && activeModal.type === 'level' && (() => {
        const lvl = activeModal.item;
        const isPending = activeModal.isPending;
        const pending = isPending ? parsePayload(lvl.pending_fee_payload) : {};

        // Proposed or official values
        const currReg = Number(lvl.registration_fee || 0);
        const displayReg = isPending && pending.registration_fee !== undefined ? Number(pending.registration_fee) : currReg;
        const isRegChanged = isPending && pending.registration_fee !== undefined && displayReg !== currReg;

        const currTest = Number(lvl.test_reg_form_fee || 0);
        const displayTest = isPending && pending.test_reg_form_fee !== undefined ? Number(pending.test_reg_form_fee) : currTest;
        const isTestChanged = isPending && pending.test_reg_form_fee !== undefined && displayTest !== currTest;

        const currAdm = Number(lvl.admission_fee || 0);
        const displayAdm = isPending && pending.admission_fee !== undefined ? Number(pending.admission_fee) : currAdm;
        const isAdmChanged = isPending && pending.admission_fee !== undefined && displayAdm !== currAdm;

        const currDev = Number(lvl.development_fee || 0);
        const displayDev = isPending && pending.development_fee !== undefined ? Number(pending.development_fee) : currDev;
        const isDevChanged = isPending && pending.development_fee !== undefined && displayDev !== currDev;

        const currBooks = Number(lvl.books_activity_fee || 0);
        const displayBooks = isPending && pending.books_activity_fee !== undefined ? Number(pending.books_activity_fee) : currBooks;
        const isBooksChanged = isPending && pending.books_activity_fee !== undefined && displayBooks !== currBooks;

        const currTuition = Number(lvl.tuition_fee || 0);
        const displayTuition = isPending && pending.tuition_fee !== undefined ? Number(pending.tuition_fee) : currTuition;
        const isTuitionChanged = isPending && pending.tuition_fee !== undefined && displayTuition !== currTuition;

        // DF Termins
        const currDf1 = Number(lvl.df_1_amount || 0);
        const displayDf1 = isPending && pending.df_1_amount !== undefined ? Number(pending.df_1_amount) : currDf1;
        const isDf1Changed = isPending && pending.df_1_amount !== undefined && displayDf1 !== currDf1;

        const currDf2 = Number(lvl.df_2_amount || 0);
        const displayDf2 = isPending && pending.df_2_amount !== undefined ? Number(pending.df_2_amount) : currDf2;
        const isDf2Changed = isPending && pending.df_2_amount !== undefined && displayDf2 !== currDf2;

        const currDf3 = Number(lvl.df_3_amount || 0);
        const displayDf3 = isPending && pending.df_3_amount !== undefined ? Number(pending.df_3_amount) : currDf3;
        const isDf3Changed = isPending && pending.df_3_amount !== undefined && displayDf3 !== currDf3;

        // Bank Account Info
        const displayBankName = isPending && pending.bank_name ? pending.bank_name : (lvl.bank_name || 'BCA (Bank Central Asia)');
        const isBankNameChanged = isPending && pending.bank_name && pending.bank_name !== lvl.bank_name;

        const displayBankAccount = isPending && pending.bank_account_number ? pending.bank_account_number : (lvl.bank_account_number || '548-098-7788');
        const isBankAccountChanged = isPending && pending.bank_account_number && pending.bank_account_number !== lvl.bank_account_number;

        const displayBankHolder = isPending && pending.bank_account_holder ? pending.bank_account_holder : (lvl.bank_account_holder || 'Yayasan Pendidikan Permata Harapan');
        const isBankHolderChanged = isPending && pending.bank_account_holder && pending.bank_account_holder !== lvl.bank_account_holder;

        return (
          <div className="adm-config-modal-overlay" onClick={closeModal}>
            <div className="adm-config-modal-box" style={{ maxWidth: 880 }} onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div className="adm-config-modal-head">
                <div className="adm-config-modal-head-title">
                  <div className="adm-config-modal-head-icon">
                    <GraduationCap size={18} />
                  </div>
                  <div>
                    <h3>
                      Fee Structure &amp; Level Settings: {lvl.level_name}
                      <span className="adm-config-modal-head-tag">{lvl.tag_code}</span>
                    </h3>
                    <p style={{ margin: 0, fontSize: '0.78rem', color: '#94a3b8' }}>
                      Review development fees, installment terms, offline assessment curriculum, and authorized signatories.
                    </p>
                  </div>
                </div>
                <button type="button" onClick={closeModal} title="Close">
                  <X size={18} />
                </button>
              </div>

              {/* MODERN SUBNAV TABS */}
              <div className="adm-modal-subnav">
                <button
                  type="button"
                  className={`adm-modal-subnav-btn ${feeModalTab === 'fees' ? 'active' : ''}`}
                  onClick={() => setFeeModalTab('fees')}
                >
                  <CreditCard size={15} />
                  <span>1. Fee Rates &amp; Development Fee</span>
                </button>
                <button
                  type="button"
                  className={`adm-modal-subnav-btn ${feeModalTab === 'status_mat' ? 'active' : ''}`}
                  onClick={() => setFeeModalTab('status_mat')}
                >
                  <BookOpen size={15} />
                  <span>2. Status &amp; Offline Assessment</span>
                </button>
                <button
                  type="button"
                  className={`adm-modal-subnav-btn ${feeModalTab === 'bank_sign' ? 'active' : ''}`}
                  onClick={() => setFeeModalTab('bank_sign')}
                >
                  <ShieldCheck size={15} />
                  <span>3. Bank Account &amp; Signatories</span>
                </button>
              </div>

              {/* Body */}
              <div className="adm-config-modal-body" style={{ padding: '20px 22px', overflowY: 'auto' }}>
                {isPending ? (
                  <div className="adm-audit-request-banner update" style={{ marginBottom: 16 }}>
                    <div className="adm-audit-badge-row">
                      <span className="afa-tag-action update">✎ Fee Schedule Revision Request</span>
                      <span className="adm-audit-status-tag">Awaiting Principal Authorization</span>
                    </div>
                    <div className="adm-audit-details-grid">
                      <div className="adm-audit-item">
                        <span className="adm-audit-label">Submitted By</span>
                        <span className="adm-audit-value">{lvl.fee_updated_by || 'Admin Admissions'}</span>
                      </div>
                      <div className="adm-audit-item">
                        <span className="adm-audit-label">Submission Date &amp; Time</span>
                        <span className="adm-audit-value">{formatDateTimeDisplayEn(lvl.fee_updated_at)}</span>
                      </div>
                      <div className="adm-audit-item">
                        <span className="adm-audit-label">Target Level</span>
                        <span className="adm-audit-value">{lvl.level_name} ({lvl.tag_code})</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{ background: '#ecfdf5', border: '1.5px solid #a7f3d0', padding: '12px 16px', borderRadius: 10, marginBottom: 16, color: '#065f46', fontSize: 13, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <CheckCircle2 size={18} color="#059669" style={{ flexShrink: 0 }} />
                    <div>
                      <strong>Status: Authorized by Principal &amp; Officially Active</strong>
                      <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#047857' }}>
                        All fee components below serve as the official schedule applied across student invoices and enrollment billing.
                      </p>
                    </div>
                  </div>
                )}

                {/* TAB 1: TARIF BIAYA & TERMIN */}
                {feeModalTab === 'fees' && (
                  <>
                    <div className="adm-form-card-section" style={{ background: '#f8fafc', borderColor: '#cbd5e1', marginBottom: 14 }}>
                      <div className="adm-form-section-head">
                        <CreditCard size={15} color="#059669" />
                        <span>Parent Cost Summary ({lvl.level_name})</span>
                        <span className="adm-form-section-head-badge">Consolidated Parent Billing</span>
                      </div>
                      <div className="adm-grid-2">
                        <div className="adm-field">
                          <label>Total Initial Upfront Cost (Reg + Test + Matr + DF Term 1)</label>
                          <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#059669', marginTop: 4 }}>
                            {formatRupiah(displayReg + displayTest + displayAdm + (displayDf1 > 0 ? displayDf1 : displayDev))}
                          </div>
                          {(isRegChanged || isTestChanged || isAdmChanged || isDf1Changed || isDevChanged) && (
                            <span className="afa-diff-sub" style={{ marginTop: 4, display: 'inline-block' }}>
                              Was: {formatRupiah(currReg + currTest + currAdm + (currDf1 > 0 ? currDf1 : currDev))}
                            </span>
                          )}
                        </div>
                        <div className="adm-field">
                          <label>Recurring Monthly Tuition Fee (SPP)</label>
                          <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#2563eb', marginTop: 4 }}>
                            {formatRupiah(displayTuition)} <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#64748b' }}>/ month</span>
                          </div>
                          {isTuitionChanged && (
                            <span className="afa-diff-sub" style={{ marginTop: 4, display: 'inline-block' }}>
                              Was: {formatRupiah(currTuition)} / month
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="adm-form-card-section">
                      <div className="adm-form-section-head">
                        <CreditCard size={15} color="#0284c7" />
                        <span>Standard Fee Schedule &amp; Monthly Tuition</span>
                        <span className="adm-form-section-head-badge">
                          {isPending ? 'Proposed Revision' : 'Auto Currency Format'}
                        </span>
                      </div>
                      <div className="adm-grid-3">
                        <div className="adm-field">
                          <label>Registration Fee (IDR)</label>
                          <CurrencyInput value={displayReg} readOnly={true} />
                          {isRegChanged && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4, fontSize: '0.74rem', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 4, padding: '2px 6px', fontWeight: 600 }}>
                              Was: {formatRupiah(currReg)}
                            </span>
                          )}
                        </div>
                        <div className="adm-field">
                          <label>Test / Reg. Form Fee (IDR)</label>
                          <CurrencyInput value={displayTest} readOnly={true} />
                          {isTestChanged && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4, fontSize: '0.74rem', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 4, padding: '2px 6px', fontWeight: 600 }}>
                              Was: {formatRupiah(currTest)}
                            </span>
                          )}
                        </div>
                        <div className="adm-field">
                          <label>Admission Fee (IDR)</label>
                          <CurrencyInput value={displayAdm} readOnly={true} />
                          {isAdmChanged && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4, fontSize: '0.74rem', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 4, padding: '2px 6px', fontWeight: 600 }}>
                              Was: {formatRupiah(currAdm)}
                            </span>
                          )}
                        </div>
                        <div className="adm-field">
                          <label>Development Fee / Uang Pangkal (IDR)</label>
                          <CurrencyInput value={displayDev} readOnly={true} />
                          {isDevChanged && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4, fontSize: '0.74rem', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 4, padding: '2px 6px', fontWeight: 600 }}>
                              Was: {formatRupiah(currDev)}
                            </span>
                          )}
                        </div>
                        <div className="adm-field">
                          <label>Books &amp; Activity Fee (IDR)</label>
                          <CurrencyInput value={displayBooks} readOnly={true} />
                          {isBooksChanged && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4, fontSize: '0.74rem', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 4, padding: '2px 6px', fontWeight: 600 }}>
                              Was: {formatRupiah(currBooks)}
                            </span>
                          )}
                        </div>
                        <div className="adm-field">
                          <label>Monthly Tuition Fee / SPP (IDR)</label>
                          <CurrencyInput value={displayTuition} readOnly={true} />
                          {isTuitionChanged && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4, fontSize: '0.74rem', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 4, padding: '2px 6px', fontWeight: 600 }}>
                              Was: {formatRupiah(currTuition)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="adm-form-card-section">
                      <div className="adm-form-section-head">
                        <Sparkles size={15} color="#0284c7" />
                        <span>Development Fee Installment Terms (DF 1 - 3)</span>
                        <span className="adm-form-section-head-badge">Terms 1 + 2 + 3 must balance</span>
                      </div>
                      <div className="adm-grid-3">
                        <div className="adm-field">
                          <label>Term DF 1 (Upon Re-registration)</label>
                          <CurrencyInput value={displayDf1} readOnly={true} />
                          {isDf1Changed && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4, fontSize: '0.74rem', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 4, padding: '2px 6px', fontWeight: 600 }}>
                              Was: {formatRupiah(currDf1)}
                            </span>
                          )}
                        </div>
                        <div className="adm-field">
                          <label>Term DF 2 (IDR)</label>
                          <CurrencyInput value={displayDf2} readOnly={true} />
                          {isDf2Changed && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4, fontSize: '0.74rem', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 4, padding: '2px 6px', fontWeight: 600 }}>
                              Was: {formatRupiah(currDf2)}
                            </span>
                          )}
                        </div>
                        <div className="adm-field">
                          <label>Term DF 3 (IDR)</label>
                          <CurrencyInput value={displayDf3} readOnly={true} />
                          {isDf3Changed && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4, fontSize: '0.74rem', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 4, padding: '2px 6px', fontWeight: 600 }}>
                              Was: {formatRupiah(currDf3)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* TAB 2: STATUS & MATERI SELEKSI */}
                {feeModalTab === 'status_mat' && (
                  <>
                    <div className="adm-form-card-section">
                      <div className="adm-form-section-head">
                        <Settings size={15} color="#0284c7" />
                        <span>Enrollment Operational Status</span>
                      </div>
                      <div className="adm-grid-2">
                        <div className="adm-field">
                          <label>Level Enrollment Status</label>
                          <input
                            type="text"
                            readOnly
                            value={lvl.is_open ? 'Open (Actively Accepting Student Registrations)' : 'Temporarily Closed / Coming Soon'}
                            style={{ background: '#f8fafc', fontWeight: 600 }}
                          />
                        </div>
                        <div className="adm-field">
                          <label>Coming Soon Note (if applicable)</label>
                          <textarea
                            rows={2}
                            readOnly
                            value={lvl.coming_soon_note || '-'}
                            style={{ background: '#f8fafc', resize: 'none', width: '100%', boxSizing: 'border-box' }}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="adm-form-card-section">
                      <div className="adm-form-section-head">
                        <BookOpen size={15} color="#0284c7" />
                        <span>Offline Assessment &amp; Observation Curriculum ({lvl.level_name})</span>
                      </div>
                      <div className="adm-grid-2">
                        <div className="adm-field">
                          <label>Assessment Stage Title</label>
                          <input
                            type="text"
                            readOnly
                            value={lvl.assessment_title || '-'}
                            style={{ background: '#f8fafc', fontWeight: 600 }}
                          />
                        </div>
                        <div className="adm-field">
                          <label>Subjects / Evaluated Aspects</label>
                          <input
                            type="text"
                            readOnly
                            value={lvl.assessment_subjects || '-'}
                            style={{ background: '#f8fafc' }}
                          />
                        </div>
                      </div>
                      <div className="adm-field" style={{ marginTop: 12 }}>
                        <label>Assessment Instructions for Parents</label>
                        <textarea
                          rows={3}
                          readOnly
                          value={lvl.assessment_description || '-'}
                          style={{ background: '#f8fafc' }}
                        />
                      </div>
                      <div className="adm-field" style={{ marginTop: 12 }}>
                        <label>Required Equipment for Offline Test</label>
                        <input
                          type="text"
                          readOnly
                          value={lvl.assessment_preparation || '-'}
                          style={{ background: '#f8fafc' }}
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* TAB 3: REKENING & PEJABAT PENANDATANGAN */}
                {feeModalTab === 'bank_sign' && (
                  <>
                    <div className="adm-form-card-section">
                      <div className="adm-form-section-head">
                        <School size={15} color="#0284c7" />
                        <span>Designated School Bank Account</span>
                        <span className="adm-form-section-head-badge">Displayed on invoices &amp; transfer guides</span>
                      </div>
                      <div className="adm-grid-3">
                        <div className="adm-field">
                          <label>School Bank Name</label>
                          <input
                            type="text"
                            readOnly
                            value={displayBankName}
                            style={{ background: '#f8fafc', fontWeight: 600 }}
                          />
                          {isBankNameChanged && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4, fontSize: '0.74rem', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 4, padding: '2px 6px', fontWeight: 600 }}>
                              Was: {lvl.bank_name}
                            </span>
                          )}
                        </div>
                        <div className="adm-field">
                          <label>Bank Account Number</label>
                          <input
                            type="text"
                            readOnly
                            value={displayBankAccount}
                            style={{ background: '#f8fafc', fontWeight: 700, letterSpacing: '0.03em' }}
                          />
                          {isBankAccountChanged && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4, fontSize: '0.74rem', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 4, padding: '2px 6px', fontWeight: 600 }}>
                              Was: {lvl.bank_account_number}
                            </span>
                          )}
                        </div>
                        <div className="adm-field">
                          <label>Account Holder Name</label>
                          <input
                            type="text"
                            readOnly
                            value={displayBankHolder}
                            style={{ background: '#f8fafc' }}
                          />
                          {isBankHolderChanged && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4, fontSize: '0.74rem', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 4, padding: '2px 6px', fontWeight: 600 }}>
                              Was: {lvl.bank_account_holder}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="adm-form-card-section">
                      <div className="adm-form-section-head">
                        <ShieldCheck size={15} color="#0284c7" />
                        <span>Authorized Document Signatories Governance</span>
                      </div>
                      <div className="adm-grid-2">
                        <div className="adm-field">
                          <label>School Principal Name</label>
                          <input
                            type="text"
                            readOnly
                            value={lvl.principal_name || 'Asnah Roidah Yulianti, S.Si., M.Pd'}
                            style={{ background: '#f8fafc', fontWeight: 600 }}
                          />
                        </div>
                        <div className="adm-field">
                          <label>Principal Title</label>
                          <input
                            type="text"
                            readOnly
                            value={lvl.principal_title || 'Principal of Secondary School'}
                            style={{ background: '#f8fafc' }}
                          />
                        </div>
                      </div>
                      <div className="adm-grid-2" style={{ marginTop: 14 }}>
                        <div className="adm-field">
                          <label>Finance PIC Name</label>
                          <input
                            type="text"
                            readOnly
                            value={lvl.finance_pic_name || 'Zahra, S.E.'}
                            style={{ background: '#f8fafc', fontWeight: 600 }}
                          />
                        </div>
                        <div className="adm-field">
                          <label>Finance Title</label>
                          <input
                            type="text"
                            readOnly
                            value={lvl.finance_pic_title || 'Finance & Treasury PIC'}
                            style={{ background: '#f8fafc' }}
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Rejection Form Input */}
                {rejectMode && (
                  <div style={{ marginTop: 16, background: '#fffdfa', border: '1.5px solid #fde68a', borderRadius: 10, padding: '14px 16px' }}>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#92400e', marginBottom: 6 }}>
                      Rejection Reason / Notes for Admin Admissions:
                    </label>
                    <textarea
                      rows={3}
                      value={rejectNote}
                      onChange={(e) => setRejectNote(e.target.value)}
                      placeholder="e.g. Tuition fee is too high, please coordinate with the finance committee..."
                      style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #cbd5e1', borderRadius: 8, padding: '8px 10px', fontSize: 13 }}
                    />
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="adm-config-modal-footer" style={{ padding: '14px 22px', borderTop: '1px solid #e2e8f0', background: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                {isPending ? (
                  rejectMode ? (
                    <>
                      <button
                        type="button"
                        className="adm-btn-outline"
                        onClick={() => setRejectMode(false)}
                        disabled={actionLoading}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="adm-btn-action-del"
                        style={{ padding: '8px 18px', fontSize: '0.84rem' }}
                        disabled={actionLoading}
                        onClick={() => handleConfirmRejectLevel(lvl.level_code)}
                      >
                        {actionLoading ? 'Saving...' : 'Confirm Rejection'}
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="adm-btn-outline"
                        onClick={closeModal}
                        disabled={actionLoading}
                      >
                        Close
                      </button>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <button
                          type="button"
                          className="adm-btn-action-del"
                          style={{ padding: '8px 16px', fontSize: '0.84rem' }}
                          disabled={actionLoading}
                          onClick={() => setRejectMode(true)}
                        >
                          <XCircle size={15} />
                          <span>Reject Revision</span>
                        </button>
                        <button
                          type="button"
                          className="adm-btn-primary"
                          style={{ background: '#059669', borderColor: '#047857' }}
                          disabled={actionLoading}
                          onClick={() => handleApproveLevelFee(lvl.level_code)}
                        >
                          <CheckCircle2 size={15} />
                          <span>{actionLoading ? 'Processing...' : 'Approve & Authorize Fees'}</span>
                        </button>
                      </div>
                    </>
                  )
                ) : (
                  <div style={{ marginLeft: 'auto' }}>
                    <button
                      type="button"
                      className="adm-btn-outline"
                      onClick={closeModal}
                    >
                      Close
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ========================================================
          UNIVERSAL CLEAN DETAIL & DECISION MODAL (PERIOD, TEST SLOT, PROMO)
          ======================================================== */}
      {activeModal && activeModal.type !== 'level' && (
        <div className="afa-modal-overlay" onClick={closeModal}>
          <div className="afa-modal-box" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="afa-modal-head">
              <div>
                <span className="afa-modal-pre">
                  {activeModal.type === 'period'
                    ? (activeModal.isPending ? 'GOVERNANCE • ADMISSION PERIOD REVIEW' : 'CONFIGURATION • OFFICIAL PERIOD DETAILS')
                    : activeModal.type === 'test_slot'
                    ? (activeModal.isPending ? 'GOVERNANCE • TEST SESSION REVIEW' : 'CONFIGURATION • OFFICIAL TEST SESSION DETAILS')
                    : (activeModal.isPending ? 'GOVERNANCE • PROMO VOUCHER REVIEW' : 'CONFIGURATION • OFFICIAL PROMO VOUCHER DETAILS')}
                </span>
                <h3>
                  {activeModal.type === 'period'
                    ? cleanTargetTitle(activeModal.item.target_label || activeModal.item.period_name)
                    : activeModal.type === 'test_slot'
                    ? cleanTargetTitle(activeModal.item.target_label || activeModal.item.session_name || activeModal.item.payload?.session_name)
                    : (activeModal.item.payload?.promo_code || activeModal.item.promo_code || 'Voucher Promo')}
                </h3>
              </div>
              <button
                type="button"
                className="afa-modal-close"
                onClick={closeModal}
                title={activeModal.type === 'period' || activeModal.type === 'test_slot' ? 'Close' : 'Tutup'}
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="afa-modal-body">
              {/* 1. PERIOD MODAL CONTENT (MATCHING ADMIN ADMISSIONS DESIGN) */}
              {activeModal.type === 'period' && (() => {
                const isPending = activeModal.isPending;
                const p = isPending ? (activeModal.item.payload || {}) : activeModal.item;
                const orig = isPending ? activeModal.item.original : null;
                const quotas = p.quotas || {};
                const origQuotas = orig?.quotas || null;

                const isCreate = isPending && (activeModal.item.action_type === 'create' || /^Tambah/i.test(activeModal.item.target_label || ''));
                const isUpdate = isPending && !isCreate && (activeModal.item.action_type === 'update' || /^Ubah|^Perbarui/i.test(activeModal.item.target_label || ''));
                const isDelete = isPending && (activeModal.item.action_type === 'delete' || /^Hapus/i.test(activeModal.item.target_label || ''));

                const periodApplicants = getApplicantsForPeriod(activeModal.item);

                return (
                  <div className="adm-modal-content-wrap">
                    {/* Governance Audit Request Banner */}
                    {isPending ? (
                      <div className={`adm-audit-request-banner ${isDelete ? 'delete' : isUpdate ? 'update' : 'create'}`}>
                        <div className="adm-audit-badge-row">
                          {isCreate && <span className="afa-tag-action create">+ New Period Addition Request</span>}
                          {isUpdate && <span className="afa-tag-action update">✎ Period Modification Request</span>}
                          {isDelete && <span className="afa-tag-action delete">✕ Period Deletion Request</span>}
                          <span className="adm-audit-status-tag">Awaiting Principal Authorization</span>
                        </div>
                        <div className="adm-audit-details-grid">
                          <div className="adm-audit-item">
                            <span className="adm-audit-label">Submitted By</span>
                            <span className="adm-audit-value">{activeModal.item.requested_by_name || 'Admin Admissions'}</span>
                          </div>
                          <div className="adm-audit-item">
                            <span className="adm-audit-label">Submission Date &amp; Time</span>
                            <span className="adm-audit-value">{formatDateTimeDisplayEn(activeModal.item.created_at)}</span>
                          </div>
                          <div className="adm-audit-item">
                            <span className="adm-audit-label">Target Module</span>
                            <span className="adm-audit-value">Admission Period &amp; Quotas</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="adm-modal-alert active">
                        <CheckCircle2 size={20} color="#16a34a" style={{ flexShrink: 0, marginTop: 2 }} />
                        <div>
                          <strong>Official &amp; Active Admission Period</strong>
                          <div>Currently active and open for parent registrations on the public admissions portal.</div>
                        </div>
                      </div>
                    )}

                    {/* Section 1: General Information & Schedule */}
                    <div className="adm-form-card-section">
                      <div className="adm-form-section-head">
                        <Sparkles size={15} color="#0284c7" />
                        <span>General Period &amp; Schedule Information</span>
                      </div>
                      <div className="adm-info-grid">
                        <div className="adm-info-item">
                          <label>Admission Period Name</label>
                          <strong>{cleanTargetTitle(p.period_name || activeModal.item.target_label)}</strong>
                        </div>
                        <div className="adm-info-item">
                          <label>Academic Year</label>
                          <strong className="adm-mono">{p.academic_year || '2026/2027'}</strong>
                        </div>
                        <div className="adm-info-item">
                          <label>Registration Opening Date</label>
                          <strong>{formatDateDisplayEn(p.start_date)}</strong>
                          {orig && orig.start_date && orig.start_date !== p.start_date && (
                            <span className="afa-diff-sub">Previously: {formatDateDisplayEn(orig.start_date)}</span>
                          )}
                        </div>
                        <div className="adm-info-item">
                          <label>Registration Closing Date</label>
                          <strong>{formatDateDisplayEn(p.end_date)}</strong>
                          {orig && orig.end_date && orig.end_date !== p.end_date && (
                            <span className="afa-diff-sub">Previously: {formatDateDisplayEn(orig.end_date)}</span>
                          )}
                        </div>
                        <div className="adm-info-item" style={{ gridColumn: 'span 2' }}>
                          <label>Operational Status</label>
                          <span className="afa-status-tag-active">
                            {(p.is_active ?? true) ? 'Active (Accepting Parent Registrations)' : 'Archived / Inactive'}
                          </span>
                        </div>
                        {p.notes && (
                          <div className="adm-info-item" style={{ gridColumn: 'span 2' }}>
                            <label>Admissions Admin Notes</label>
                            <span className="afa-italic-notes">"{p.notes}"</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Section 2: Quota Allocation per Education Level */}
                    <div className="adm-form-card-section">
                      <div className="adm-form-section-head">
                        <Layers size={15} color="#0284c7" />
                        <span>Seat Quota Allocation per Education Level</span>
                        {isUpdate && origQuotas && (
                          <span className="adm-form-section-head-badge">Original vs Proposed</span>
                        )}
                      </div>
                      <div className="adm-grid-4">
                        {[
                          { key: 'preschool', name: 'Preschool', tag: 'PS', bg: '#ecfdf5', color: '#047857' },
                          { key: 'kindergarten', name: 'Kindergarten', tag: 'KG', bg: '#e0f2fe', color: '#0284c7' },
                          { key: 'primary', name: 'Primary (SD)', tag: 'PRI', bg: '#eff6ff', color: '#1d4ed8' },
                          { key: 'secondary', name: 'Secondary (SMP)', tag: 'SEC', bg: '#f5f3ff', color: '#6d28d9' },
                        ].map((item) => {
                          const proposedVal = quotas[item.key] ?? 0;
                          const originalVal = origQuotas ? (origQuotas[item.key] ?? 0) : null;
                          const isDiff = originalVal !== null && originalVal !== proposedVal;

                          return (
                            <div key={item.key} className="afa-quota-tile" style={{ background: item.bg }}>
                              <div className="afa-quota-tile-top">
                                <span className="afa-quota-tile-tag" style={{ color: item.color }}>{item.tag}</span>
                                <span className="afa-quota-tile-name">{item.name}</span>
                              </div>
                              <div className="afa-quota-tile-val" style={{ color: item.color }}>
                                {proposedVal} <span className="afa-quota-unit">Seats</span>
                              </div>
                              {isDiff && (
                                <div className="afa-quota-diff-badge">
                                  Was: {originalVal} Seats
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Section 3: Registered Applicants in this Period */}
                    <div className="adm-form-card-section">
                      <div className="adm-form-section-head">
                        <Users size={15} color="#0284c7" />
                        <span>Registered Applicants in this Period ({periodApplicants.length})</span>
                        <span className="adm-form-section-head-badge">Live Enrollment Records</span>
                      </div>
                      {periodApplicants.length === 0 ? (
                        <div className="adm-mini-empty">
                          No students registered in this admission period yet.
                        </div>
                      ) : (
                        <div className="adm-mini-table-wrap">
                          <table className="adm-mini-table">
                            <thead>
                              <tr>
                                <th>Reg. No &amp; Student Name</th>
                                <th>Target Level</th>
                                <th>Parent / Contact</th>
                                <th>Registered Date</th>
                                <th>Application Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {periodApplicants.map((app) => (
                                <tr key={app.id}>
                                  <td>
                                    <div style={{ fontWeight: 700, color: '#0f172a' }}>{app.child_full_name}</div>
                                    <div className="adm-sub-text" style={{ fontFamily: 'monospace' }}>{app.registration_no}</div>
                                  </td>
                                  <td>
                                    <span className="adm-mono">{app.level_name || app.grade_target || '-'}</span>
                                  </td>
                                  <td>
                                    <div>{app.applicant_name || '-'}</div>
                                    <div className="adm-sub-text">{app.applicant_phone || '-'}</div>
                                  </td>
                                  <td>
                                    {formatDateDisplayEn(app.created_at)}
                                  </td>
                                  <td>
                                    <span className={`adm-status-pill ${app.status_code === 'enrolled' ? 'adm-badge-green' : app.status_code === 'verified' ? 'adm-badge-blue' : 'adm-badge-orange'}`}>
                                      {(app.status_code || 'SUBMITTED').toUpperCase()}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* 3. TEST SLOT MODAL CONTENT */}
              {activeModal.type === 'test_slot' && (() => {
                const isPending = activeModal.isPending;
                const s = isPending ? (activeModal.item.payload || {}) : activeModal.item;
                const orig = isPending ? activeModal.item.original : null;

                const isCreate = isPending && (activeModal.item.action_type === 'create' || /^Tambah/i.test(activeModal.item.target_label || ''));
                const isUpdate = isPending && !isCreate && (activeModal.item.action_type === 'update' || /^Ubah|^Perbarui/i.test(activeModal.item.target_label || ''));
                const isDelete = isPending && (activeModal.item.action_type === 'delete' || /^Hapus/i.test(activeModal.item.target_label || ''));

                const slotAttendees = getApplicantsForSlot(activeModal.item);
                const displaySlotQuota = Number(s.slot_quota || 15);
                const isFull = !isPending && slotAttendees.length >= displaySlotQuota;

                return (
                  <div className="adm-modal-content-wrap">
                    {/* Governance Audit Request Banner */}
                    {isPending ? (
                      <div className={`adm-audit-request-banner ${isDelete ? 'delete' : isUpdate ? 'update' : 'create'}`}>
                        <div className="adm-audit-badge-row">
                          {isCreate && <span className="afa-tag-action create">+ New Session Addition Request</span>}
                          {isUpdate && <span className="afa-tag-action update">✎ Session Modification Request</span>}
                          {isDelete && <span className="afa-tag-action delete">✕ Session Deletion Request</span>}
                          <span className="adm-audit-status-tag">Awaiting Principal Authorization</span>
                        </div>
                        <div className="adm-audit-details-grid">
                          <div className="adm-audit-item">
                            <span className="adm-audit-label">Submitted By</span>
                            <span className="adm-audit-value">{activeModal.item.requested_by_name || 'Admin Admissions'}</span>
                          </div>
                          <div className="adm-audit-item">
                            <span className="adm-audit-label">Submission Date &amp; Time</span>
                            <span className="adm-audit-value">{formatDateTimeDisplayEn(activeModal.item.created_at)}</span>
                          </div>
                          <div className="adm-audit-item">
                            <span className="adm-audit-label">Target Module</span>
                            <span className="adm-audit-value">Entrance Assessment &amp; Test Sessions</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="adm-modal-alert active">
                        <CheckCircle2 size={20} color="#16a34a" style={{ flexShrink: 0, marginTop: 2 }} />
                        <div>
                          <strong>Official &amp; Active Test Session</strong>
                          <div>Currently active and ready for applicant scheduling in the admissions system.</div>
                        </div>
                      </div>
                    )}

                    {/* Section 1: Session & Schedule Details */}
                    <div className="adm-form-card-section">
                      <div className="adm-form-section-head">
                        <Calendar size={15} color="#0284c7" />
                        <span>Session &amp; Schedule Details</span>
                      </div>
                      <div className="adm-info-grid">
                        <div className="adm-info-item">
                          <label>Session Name</label>
                          <strong>{cleanTargetTitle(s.session_name || activeModal.item.target_label)}</strong>
                        </div>
                        <div className="adm-info-item">
                          <label>Target Education Level</label>
                          <strong className="adm-mono">{s.level_code === 'all' || !s.level_code ? 'All Education Levels' : s.level_code.toUpperCase()}</strong>
                          {orig && orig.level_code && orig.level_code !== s.level_code && (
                            <span className="afa-diff-sub">Previously: {orig.level_code.toUpperCase()}</span>
                          )}
                        </div>
                        <div className="adm-info-item">
                          <label>Assessment Date</label>
                          <strong>{formatDateDisplayEn(s.test_date)}</strong>
                          {orig && orig.test_date && orig.test_date !== s.test_date && (
                            <span className="afa-diff-sub">Previously: {formatDateDisplayEn(orig.test_date)}</span>
                          )}
                        </div>
                        <div className="adm-info-item">
                          <label>Execution Time</label>
                          <strong>{s.test_time || '09:00 - 11:00 WIB'}</strong>
                          {orig && orig.test_time && orig.test_time !== s.test_time && (
                            <span className="afa-diff-sub">Previously: {orig.test_time}</span>
                          )}
                        </div>
                        <div className="adm-info-item" style={{ gridColumn: 'span 2' }}>
                          <label>Campus Location &amp; Room</label>
                          <span>{s.campus_location} &mdash; <strong>{s.room_name || 'Assessment Room'}</strong></span>
                          {orig && (orig.room_name !== s.room_name || orig.campus_location !== s.campus_location) && (
                            <span className="afa-diff-sub">Previously: {orig.campus_location} &mdash; {orig.room_name}</span>
                          )}
                        </div>
                        {s.custom_notes && (
                          <div className="adm-info-item" style={{ gridColumn: 'span 2' }}>
                            <label>Special Instructions / Student Equipment</label>
                            <span className="afa-italic-notes">"{s.custom_notes}"</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Section 2: Room Capacity & Booking Status */}
                    <div className="adm-form-card-section">
                      <div className="adm-form-section-head">
                        <Layers size={15} color="#0284c7" />
                        <span>Room Capacity &amp; Booking Status</span>
                      </div>
                      <div className="adm-info-grid">
                        <div className="adm-info-item">
                          <label>Participant Seat Capacity</label>
                          <strong className="adm-mono">{displaySlotQuota} Seats</strong>
                          {orig && orig.slot_quota && Number(orig.slot_quota) !== displaySlotQuota && (
                            <span className="afa-diff-sub">Previously: {orig.slot_quota} Seats</span>
                          )}
                        </div>
                        {!isPending && (
                          <>
                            <div className="adm-info-item">
                              <label>Currently Scheduled</label>
                              <strong style={{ color: isFull ? '#dc2626' : '#0369a1' }}>
                                {slotAttendees.length} / {displaySlotQuota} Students
                              </strong>
                            </div>
                            <div className="adm-info-item">
                              <label>Remaining Available Seats</label>
                              <strong>{Math.max(0, displaySlotQuota - slotAttendees.length)} Seats</strong>
                            </div>
                            <div className="adm-info-item">
                              <label>Session Availability</label>
                              <span className={isFull ? 'afa-tag-action delete' : 'afa-tag-action active'}>
                                {isFull ? 'Full (Capacity Reached)' : 'Available for Booking'}
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Section 3: Scheduled Applicants for this Test Session */}
                    <div className="adm-form-card-section">
                      <div className="adm-form-section-head">
                        <UserCheck size={15} color="#0284c7" />
                        <span>Scheduled Applicants for this Test Session ({slotAttendees.length} / {displaySlotQuota} Seats)</span>
                        <span className="adm-form-section-head-badge">Live Participant Records</span>
                      </div>
                      {slotAttendees.length === 0 ? (
                        <div className="adm-mini-empty">
                          No applicants scheduled for this test session yet.
                        </div>
                      ) : (
                        <div className="adm-mini-table-wrap">
                          <table className="adm-mini-table">
                            <thead>
                              <tr>
                                <th>Reg. No &amp; Student Name</th>
                                <th>Target Level</th>
                                <th>Parent / Contact</th>
                                <th>Room &amp; Time</th>
                                <th>Test Result / Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {slotAttendees.map((app) => (
                                <tr key={app.id}>
                                  <td>
                                    <div style={{ fontWeight: 700, color: '#0f172a' }}>{app.child_full_name}</div>
                                    <div className="adm-sub-text" style={{ fontFamily: 'monospace' }}>{app.registration_no}</div>
                                  </td>
                                  <td>
                                    <span className="adm-mono">{app.level_name || app.grade_target || '-'}</span>
                                  </td>
                                  <td>
                                    <div>{app.applicant_name || '-'}</div>
                                    <div className="adm-sub-text">{app.applicant_phone || '-'}</div>
                                  </td>
                                  <td>
                                    <div>{s.room_name || 'Assessment Room'}</div>
                                    <div className="adm-sub-text">{s.test_time || '09:00 - 11:00 WIB'}</div>
                                  </td>
                                  <td>
                                    <span className={`adm-status-pill ${app.test_status === 'passed' ? 'adm-badge-green' : app.test_status === 'failed' ? 'adm-badge-red' : 'adm-badge-orange'}`}>
                                      {(app.test_status || app.test_result || 'SCHEDULED').toUpperCase()}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* 4. PROMO CODE MODAL CONTENT */}
              {activeModal.type === 'promo_code' && (() => {
                const isPending = activeModal.isPending;
                const pr = isPending ? (activeModal.item.payload || {}) : activeModal.item;
                const orig = isPending ? activeModal.item.original : null;

                const isCreate = isPending && (activeModal.item.action_type === 'create' || /^Tambah/i.test(activeModal.item.target_label || ''));
                const isUpdate = isPending && !isCreate && (activeModal.item.action_type === 'update' || /^Ubah|^Perbarui/i.test(activeModal.item.target_label || ''));
                const isDelete = isPending && (activeModal.item.action_type === 'delete' || /^Hapus/i.test(activeModal.item.target_label || ''));

                const promoApplicants = getApplicantsForPromo(pr);
                const combinedUsers = modalPromoUsers.length > 0 ? modalPromoUsers : promoApplicants;
                const displayPromoQuota = Number(pr.max_quota || 100);
                const isFull = !isPending && combinedUsers.length >= displayPromoQuota;
                const isExpired = !isPending && pr.valid_until && new Date(pr.valid_until) < new Date();

                return (
                  <div className="adm-modal-content-wrap">
                    {/* Governance Audit Request Banner */}
                    {isPending ? (
                      <div className={`adm-audit-request-banner ${isDelete ? 'delete' : isUpdate ? 'update' : 'create'}`}>
                        <div className="adm-audit-badge-row">
                          {isCreate && <span className="afa-tag-action create">+ New Voucher Addition Request</span>}
                          {isUpdate && <span className="afa-tag-action update">✎ Voucher Modification Request</span>}
                          {isDelete && <span className="afa-tag-action delete">✕ Voucher Deletion Request</span>}
                          <span className="adm-audit-status-tag">Awaiting Principal Authorization</span>
                        </div>
                        <div className="adm-audit-details-grid">
                          <div className="adm-audit-item">
                            <span className="adm-audit-label">Submitted By</span>
                            <span className="adm-audit-value">{activeModal.item.requested_by_name || 'Admin Admissions'}</span>
                          </div>
                          <div className="adm-audit-item">
                            <span className="adm-audit-label">Submission Date &amp; Time</span>
                            <span className="adm-audit-value">{formatDateTimeDisplayEn(activeModal.item.created_at)}</span>
                          </div>
                          <div className="adm-audit-item">
                            <span className="adm-audit-label">Target Module</span>
                            <span className="adm-audit-value">Promo Codes &amp; Discount Vouchers</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="adm-modal-alert active">
                        <CheckCircle2 size={20} color="#16a34a" style={{ flexShrink: 0, marginTop: 2 }} />
                        <div>
                          <strong>Official &amp; Active Promo Voucher</strong>
                          <div>Currently active and redeemable by applicants on student billing and invoice forms.</div>
                        </div>
                      </div>
                    )}

                    {/* Section 1: Voucher Code & Discount Benefits */}
                    <div className="adm-form-card-section">
                      <div className="adm-form-section-head">
                        <Ticket size={15} color="#0284c7" />
                        <span>Voucher Code &amp; Discount Benefits</span>
                      </div>
                      <div className="adm-info-grid">
                        <div className="adm-info-item">
                          <label>Voucher Code</label>
                          <strong className="adm-mono" style={{ color: '#2563eb', fontSize: '1.05rem', letterSpacing: '0.04em' }}>
                            {pr.promo_code}
                          </strong>
                        </div>
                        <div className="adm-info-item">
                          <label>Promo Program Title</label>
                          <strong>{cleanTargetTitle(pr.promo_title || activeModal.item.target_label)}</strong>
                        </div>
                        <div className="adm-info-item">
                          <label>Discount Value</label>
                          <strong className="adm-mono" style={{ color: '#059669', fontSize: '1.05rem' }}>
                            {pr.discount_type === 'percentage'
                              ? `${pr.discount_value}% from Development Fee (DF)`
                              : formatRupiah(pr.discount_value)}
                          </strong>
                          {orig && (orig.discount_value !== pr.discount_value || orig.discount_type !== pr.discount_type) && (
                            <span className="afa-diff-sub">
                              Previously: {orig.discount_type === 'percentage' ? `${orig.discount_value}% from DF` : formatRupiah(orig.discount_value)}
                            </span>
                          )}
                        </div>
                        <div className="adm-info-item">
                          <label>Applicable Education Levels</label>
                          <strong className="adm-mono">
                            {pr.applies_to_levels === 'all' || !pr.applies_to_levels ? 'All Education Levels' : pr.applies_to_levels.toUpperCase()}
                          </strong>
                          {orig && orig.applies_to_levels && orig.applies_to_levels !== pr.applies_to_levels && (
                            <span className="afa-diff-sub">Previously: {orig.applies_to_levels.toUpperCase()}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Section 2: Quota Limits & Validity Period */}
                    <div className="adm-form-card-section">
                      <div className="adm-form-section-head">
                        <Layers size={15} color="#0284c7" />
                        <span>Quota Limits &amp; Validity Schedule</span>
                      </div>
                      <div className="adm-info-grid">
                        <div className="adm-info-item">
                          <label>Maximum Usage Quota</label>
                          <strong className="adm-mono">{displayPromoQuota} Redemptions</strong>
                          {orig && orig.max_quota && Number(orig.max_quota) !== displayPromoQuota && (
                            <span className="afa-diff-sub">Previously: {orig.max_quota} Redemptions</span>
                          )}
                        </div>
                        <div className="adm-info-item">
                          <label>Validity Expiration Date</label>
                          <strong>{pr.valid_until ? `Valid until ${formatDateDisplayEn(pr.valid_until)}` : 'Lifetime (No Expiry Date)'}</strong>
                          {orig && orig.valid_until !== pr.valid_until && (
                            <span className="afa-diff-sub">
                              Previously: {orig.valid_until ? formatDateDisplayEn(orig.valid_until) : 'Lifetime'}
                            </span>
                          )}
                        </div>
                        {!isPending && (
                          <>
                            <div className="adm-info-item">
                              <label>Currently Redeemed</label>
                              <strong style={{ color: isFull ? '#dc2626' : '#0369a1' }}>
                                {combinedUsers.length} / {displayPromoQuota} Vouchers Used
                              </strong>
                            </div>
                            <div className="adm-info-item">
                              <label>Voucher Status</label>
                              <span className={isExpired ? 'afa-tag-action delete' : isFull ? 'afa-tag-action delete' : 'afa-tag-action active'}>
                                {isExpired ? 'Expired' : isFull ? 'Quota Exhausted' : 'Active & Redeemable'}
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Section 3: Applicants Redeeming this Voucher */}
                    <div className="adm-form-card-section">
                      <div className="adm-form-section-head">
                        <Users size={15} color="#0284c7" />
                        <span>Applicants Redeeming this Voucher ({combinedUsers.length} / {displayPromoQuota})</span>
                        <span className="adm-form-section-head-badge">Live Redemption Records</span>
                      </div>
                      {loadingPromoUsers ? (
                        <div className="adm-mini-empty">Loading voucher redemption records...</div>
                      ) : combinedUsers.length === 0 ? (
                        <div className="adm-mini-empty">
                          No applicants have redeemed this promo voucher yet.
                        </div>
                      ) : (
                        <div className="adm-mini-table-wrap">
                          <table className="adm-mini-table">
                            <thead>
                              <tr>
                                <th>Reg. No &amp; Student Name</th>
                                <th>Target Level</th>
                                <th>Parent / Contact</th>
                                <th>Discount Benefit</th>
                                <th>Registration Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {combinedUsers.map((user) => (
                                <tr key={user.id}>
                                  <td>
                                    <div style={{ fontWeight: 700, color: '#0f172a' }}>{user.child_full_name}</div>
                                    <div className="adm-sub-text" style={{ fontFamily: 'monospace' }}>{user.registration_no}</div>
                                  </td>
                                  <td>
                                    <span className="adm-mono">{user.level_name || user.level_code?.toUpperCase() || '-'}</span>
                                  </td>
                                  <td>
                                    <div>{user.parent_name || user.applicant_name || '-'}</div>
                                    <div className="adm-sub-text">{user.parent_phone || user.applicant_phone || '-'}</div>
                                  </td>
                                  <td>
                                    <strong style={{ color: '#059669' }}>
                                      {user.discount_amount ? formatRupiah(user.discount_amount) : (pr.discount_type === 'percentage' ? `${pr.discount_value}% DF` : formatRupiah(pr.discount_value))}
                                    </strong>
                                  </td>
                                  <td>
                                    <span className="adm-status-pill adm-badge-green">
                                      {(user.status_code || 'APPLIED').toUpperCase()}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Rejection Form Input (when user clicks Tolak) */}
              {rejectMode && (
                <div className="afa-modal-reject-box">
                  <label>
                    Reason / Notes for Rejection (sent to Admissions Admin):
                  </label>
                  <textarea
                    rows={3}
                    value={rejectNote}
                    onChange={(e) => setRejectNote(e.target.value)}
                    placeholder={
                      activeModal.type === 'period'
                        ? 'e.g. Please revise Primary quota or extend registration end date...'
                        : activeModal.type === 'level'
                        ? 'e.g. Development fee adjustment needs reconsideration with finance team...'
                        : activeModal.type === 'test_slot'
                        ? 'e.g. Assessment Room A is reserved on this date, please assign to Room B...'
                        : 'e.g. Discount percentage is too high, please adjust to 10% maximum...'
                    }
                    required
                  />
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="afa-modal-foot">
              {activeModal.isPending ? (
                <>
                  <div className="afa-foot-left-notice">
                    <ShieldCheck size={14} color="#64748b" />
                    <span>
                      {activeModal.type === 'period'
                        ? 'Principal authorization will take effect immediately across all school systems.'
                        : activeModal.type === 'level'
                        ? 'Principal authorization will take effect immediately across all student invoices and billing.'
                        : activeModal.type === 'test_slot'
                        ? 'Principal authorization will take effect immediately across all entrance test schedules.'
                        : 'Principal authorization will take effect immediately across all student payment portals.'}
                    </span>
                  </div>
                  <div className="afa-foot-btns">
                    {rejectMode ? (
                      <>
                        <button
                          type="button"
                          className="afa-btn-modal-cancel"
                          onClick={() => setRejectMode(false)}
                          disabled={actionLoading}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="afa-btn-modal-reject-confirm"
                          disabled={actionLoading}
                          onClick={() => {
                            if (activeModal.type === 'level') {
                              handleConfirmRejectLevel(activeModal.item.level_code);
                            } else {
                              handleConfigDecision(activeModal.item.id, 'reject', rejectNote);
                            }
                          }}
                        >
                          {actionLoading ? 'Saving...' : 'Confirm Rejection'}
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="afa-btn-modal-reject"
                          disabled={actionLoading}
                          onClick={() => setRejectMode(true)}
                        >
                          <XCircle size={14} />
                          <span>
                            {activeModal.type === 'period'
                              ? 'Reject Period'
                              : activeModal.type === 'test_slot'
                              ? 'Reject Session'
                              : 'Reject Voucher'}
                          </span>
                        </button>
                        <button
                          type="button"
                          className="afa-btn-modal-approve"
                          disabled={actionLoading}
                          onClick={() => {
                            handleConfigDecision(activeModal.item.id, 'approve');
                          }}
                        >
                          <CheckCircle2 size={14} />
                          <span>
                            {actionLoading
                              ? 'Processing...'
                              : activeModal.type === 'period'
                              ? 'Approve & Authorize Period'
                              : activeModal.type === 'test_slot'
                              ? 'Approve & Authorize Session'
                              : 'Approve & Authorize Voucher'}
                          </span>
                        </button>
                      </>
                    )}
                  </div>
                </>
              ) : (
                <div className="afa-foot-btns right-only">
                  <button type="button" className="afa-btn-modal-close" onClick={closeModal}>
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
